// Supplier-order items section — visual mirror of OrderProductsSection.
// Same operational fields as the customer page, with product identity,
// dimensions and the catalog action grouped into one readable product column.
// Supplier purchase columns are shown only when the caller passes supplier
// purchase-price visibility.
//
// Live edit via the existing PUT /supplier-orders/:id endpoint; the backend
// replaces items wholesale when `items` is present in the payload
// (see supplierOrderService.buildSupplierOrderPayload).
//
// Unit (m²/m³/szt) is preserved per-row from the wizard but is NOT editable
// from the detail page — matches customer-side OrderProductsSection (its
// `unit` field is also wizard-set only, not exposed as a table column).
// Unit shows as a small grey suffix on the displayed quantity so the
// information isn't invisible; new rows added on the detail page default
// to 'pcs' and keep that until recreated via the wizard.
import React, { useEffect, useState } from 'react';
import {
  Alert,
  App,
  Button,
  Card,
  Input,
  InputNumber,
  Select,
  Space,
  Tag,
  Table,
  Tooltip,
} from 'antd';
import type { TableColumnsType } from 'antd';
import { CloseOutlined, DeleteOutlined, EditOutlined, InboxOutlined, PlusOutlined, SaveOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { formatCurrency } from '../../../utils/currency';
import { formatDate } from '../../../utils/dateUtils';
import { formatProductDimensions } from '../../../utils/productDimensions';
import { MAX_AMOUNT, MAX_DIMENSION, MIN_QUANTITY, SupplierOrderDto, SupplierOrderItemDto, SupplierOrderLeftoverReceiptDto } from '../../../dto/supplierOrder';
import { SupplierOrderItemPayload, updateSupplierOrder } from '../api/supplierOrdersApi';
import { buildSupplierOrderItemsUpdateData } from '../supplierOrderDetailEditUtils';
import { formatSupplierOrderQuantityValue, formatSupplierOrderUnit } from '../supplierOrderUnits';
import { VariantPicker } from '../../../components/inventory';
import { ProductVariant } from '../../../types/pz';
import { ORDER_CURRENCIES } from '../currencies';
import {
  buildSupplierOrderVariantAutofill,
  isDefaultPurchaseAutofillApplied,
} from '../supplierOrderVariantUtils';
import {
  renderSectionTitle,
  SECTION_CARD_BODY_STYLE,
  SECTION_CARD_HEAD_STYLE,
  SECTION_CARD_STYLE,
} from '../orderDetailLayout';
import { getOrderProductDisplayName } from '../orderProductDisplay';
import './SupplierOrderItemsSection.css';

const MAX_ITEMS = 200;
const PRODUCT_NAME_TEXTAREA_AUTO_SIZE = { minRows: 2, maxRows: 2 };
const ITEM_COLUMN_WIDTHS = {
  product: 300,
  quantity: 150,
  unitCost: 170,
  currency: 112,
  total: 180,
  actions: 50,
  leftover: 190,
} as const;
interface EditableItem {
  key: string;
  id?: string | null;
  productVariantId?: string | null;
  name: string;
  displayName: string;
  quantity: number;
  unit: SupplierOrderItemPayload['unit'];
  width?: number | null;
  height?: number | null;
  length?: number | null;
  thickness?: number | null;
  productCategoryName?: string | null;
  productCategoryColor?: string | null;
  productCategoryDimensionDisplayProfile?: string | null;
  currency?: string;
  unitCost?: number;
  totalCostPLN?: number;
  exchangeRateToPLN?: number;
  exchangeRateToPLNSource?: string;
  exchangeRateToPLNTableNumber?: string;
  exchangeRateToPLNEffectiveDate?: string;
  defaultPurchasePrice?: number | null;
  defaultPurchaseCurrency?: string | null;
  description?: string | null;
  leftoverReceipt?: SupplierOrderLeftoverReceiptDto;
}

interface SupplierOrderItemsSectionProps {
  supplierOrder: SupplierOrderDto;
  canEdit: boolean;
  canViewSupplierPurchasePrices: boolean;
  purchasePricesIncomplete?: boolean;
  onSaved: () => Promise<unknown>;
  onCreateLeftoverPZ?: (itemId: string) => Promise<unknown>;
  leftoverCreatingItemId?: string | null;
}

const toEditableRows = (items: SupplierOrderItemDto[] = []): EditableItem[] =>
  items.map((item, index) => ({
    key: item.id || `row-${index}`,
    id: item.id || null,
    productVariantId: item.productVariantId ?? null,
    name: item.name,
    displayName: getOrderProductDisplayName(item, item.name),
    quantity: item.quantity,
    unit: (item.unit as SupplierOrderItemPayload['unit']) || 'pcs',
    width: item.width ?? null,
    height: item.height ?? null,
    length: item.length ?? null,
    thickness: item.thickness ?? null,
    productCategoryName: item.productCategoryName ?? null,
    productCategoryColor: item.productCategoryColor ?? null,
    productCategoryDimensionDisplayProfile: item.productCategoryDimensionDisplayProfile ?? null,
    currency: item.currency ?? undefined,
    unitCost: item.unitCost,
    totalCostPLN: item.totalCostPLN,
    exchangeRateToPLN: item.exchangeRateToPLN,
    exchangeRateToPLNSource: item.exchangeRateToPLNSource,
    exchangeRateToPLNTableNumber: item.exchangeRateToPLNTableNumber,
    exchangeRateToPLNEffectiveDate: item.exchangeRateToPLNEffectiveDate,
    description: item.description ?? null,
    leftoverReceipt: item.leftoverReceipt,
  }));

export const SupplierOrderItemsSection: React.FC<SupplierOrderItemsSectionProps> = ({
  supplierOrder,
  canEdit,
  canViewSupplierPurchasePrices,
  purchasePricesIncomplete = false,
  onSaved,
  onCreateLeftoverPZ,
  leftoverCreatingItemId = null,
}) => {
  const { t, i18n } = useTranslation(['orders', 'common']);
  const { message } = App.useApp();
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [rows, setRows] = useState<EditableItem[]>(() => toEditableRows(supplierOrder.items));

  useEffect(() => {
    if (!editing) {
      setRows(toEditableRows(supplierOrder.items));
    }
    // Refresh the local buffer only when the underlying items change AND we
    // are not currently editing — protects the in-progress edit from being
    // clobbered by a parallel refresh.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [supplierOrder.items]);

  const startEditing = () => {
    setRows(toEditableRows(supplierOrder.items));
    if ((supplierOrder.items?.length || 0) === 0) {
      setRows([{ key: 'row-0', name: '', displayName: '', quantity: 1, unit: 'pcs', unitCost: 0 }]);
    }
    setEditing(true);
  };

  const cancelEditing = () => {
    setEditing(false);
    setRows(toEditableRows(supplierOrder.items));
  };

  const updateRow = (key: string, patch: Partial<EditableItem>) => {
    setRows((current) => current.map((row) => (row.key === key ? { ...row, ...patch } : row)));
  };

  const handleVariantChange = (key: string, variantId: string | undefined, variant?: ProductVariant) => {
    const patch: Partial<EditableItem> = {
      productVariantId: variantId || null,
      width: null,
      height: null,
      length: null,
      thickness: null,
      productCategoryName: null,
      productCategoryColor: null,
      productCategoryDimensionDisplayProfile: null,
      defaultPurchasePrice: null,
      defaultPurchaseCurrency: null,
    };

    if (variant) {
      const autofill = buildSupplierOrderVariantAutofill(variant);
      patch.name = autofill.name;
      patch.displayName = getOrderProductDisplayName({
        name: autofill.name,
        productVariantId: variant.id,
        productCode: variant.product?.code,
        productEan: variant.ean,
      }, autofill.name);
      patch.unit = autofill.unit;
      patch.width = autofill.width ?? null;
      patch.height = autofill.height ?? null;
      patch.length = autofill.length ?? null;
      patch.thickness = autofill.thickness ?? null;
      patch.productCategoryName = autofill.productCategoryName ?? null;
      patch.productCategoryColor = autofill.productCategoryColor ?? null;
      patch.productCategoryDimensionDisplayProfile = autofill.productCategoryDimensionDisplayProfile ?? null;
      if (canViewSupplierPurchasePrices && autofill.defaultPurchasePrice != null) {
        patch.unitCost = autofill.defaultPurchasePrice;
        if (
          autofill.defaultPurchaseCurrency
          && ORDER_CURRENCIES.includes(autofill.defaultPurchaseCurrency as typeof ORDER_CURRENCIES[number])
        ) {
          patch.currency = autofill.defaultPurchaseCurrency;
        }
      }
      patch.defaultPurchasePrice = autofill.defaultPurchasePrice ?? null;
      patch.defaultPurchaseCurrency = autofill.defaultPurchaseCurrency ?? null;
    }

    updateRow(key, patch);
  };

  const addRow = () => {
    setRows((current) => [
      ...current,
      {
        key: `row-${current.length}-${Math.random().toString(36).slice(2, 6)}`,
        name: '',
        displayName: '',
        quantity: 1,
        unit: 'pcs',
        currency: supplierOrder.currency || 'PLN',
        unitCost: 0,
      },
    ]);
  };

  const removeRow = (key: string) => {
    setRows((current) => (current.length > 1 ? current.filter((row) => row.key !== key) : current));
  };

  const validate = (): { ok: true; payload: SupplierOrderItemPayload[] } | { ok: false; reason: string } => {
    if (rows.length > MAX_ITEMS) {
      return { ok: false, reason: t('orders:supplierOrders.detail.tooManyItems', { max: MAX_ITEMS }) };
    }
    const payload: SupplierOrderItemPayload[] = [];
    for (const row of rows) {
      const name = row.name?.trim();
      if (!name) return { ok: false, reason: t('orders:supplierOrders.detail.itemNameRequired') };
      const qty = Number(row.quantity);
      if (!Number.isFinite(qty) || qty <= 0) return { ok: false, reason: t('orders:supplierOrders.detail.itemQuantityRequired') };
      // Same floor as supplierOrderQuantityRule (precision={3} input step).
      if (qty < MIN_QUANTITY || qty > MAX_AMOUNT) return { ok: false, reason: t('orders:supplierOrders.form.quantityPositiveInvalid', { max: MAX_AMOUNT }) };
      const cost = Number(row.unitCost ?? 0);
      if (!Number.isFinite(cost) || cost < 0) return { ok: false, reason: t('orders:supplierOrders.detail.itemUnitCostRequired') };
      if (cost > MAX_AMOUNT) return { ok: false, reason: t('orders:supplierOrders.form.amountInvalid', { max: MAX_AMOUNT }) };
      const currency = row.currency || supplierOrder.currency || 'PLN';
      if (!ORDER_CURRENCIES.includes(currency as typeof ORDER_CURRENCIES[number])) {
        return { ok: false, reason: t('orders:modal.validation.currencyRequired') };
      }
      for (const dim of [row.length, row.width, row.thickness]) {
        if (dim != null && (!Number.isFinite(Number(dim)) || Number(dim) < 0 || Number(dim) > MAX_DIMENSION)) {
          return { ok: false, reason: t('orders:supplierOrders.form.dimensionInvalid', { max: MAX_DIMENSION }) };
        }
      }
      payload.push({
        id: row.id || null,
        name,
        quantity: qty,
        unit: row.unit || 'pcs',
        width: row.width ?? null,
        length: row.length ?? null,
        thickness: row.thickness ?? null,
        currency,
        unitCost: cost,
        productVariantId: row.productVariantId || null,
        description: row.description || null,
      });
    }
    return { ok: true, payload };
  };

  const handleSave = async () => {
    if (!supplierOrder.id) return;
    const result = validate();
    if (!result.ok) {
      message.error(result.reason);
      return;
    }
    setSaving(true);
    try {
      await updateSupplierOrder(
        supplierOrder.id,
        buildSupplierOrderItemsUpdateData(supplierOrder, result.payload)
      );
      message.success(t('orders:supplierOrders.detail.updateSuccess'));
      setEditing(false);
      await onSaved();
    } catch (error: any) {
      message.error(error.response?.data?.message || t('orders:supplierOrders.detail.updateError'));
    } finally {
      setSaving(false);
    }
  };

  const dataSource = editing ? rows : toEditableRows(supplierOrder.items);
  const canViewCosts = canViewSupplierPurchasePrices;
  const trailingColumnWidth = editing
    ? ITEM_COLUMN_WIDTHS.actions
    : onCreateLeftoverPZ ? ITEM_COLUMN_WIDTHS.leftover : 0;
  const tableScrollWidth = ITEM_COLUMN_WIDTHS.product
    + ITEM_COLUMN_WIDTHS.quantity
    + (canViewCosts
      ? ITEM_COLUMN_WIDTHS.unitCost + ITEM_COLUMN_WIDTHS.currency + ITEM_COLUMN_WIDTHS.total
      : 0)
    + trailingColumnWidth;

  const formatPurchaseAmount = (amount: number, currency?: string) => (
    currency ? formatCurrency(amount, currency) : '-'
  );

  const renderDefaultPurchaseHint = (record: EditableItem) => {
    if (!isDefaultPurchaseAutofillApplied(record)) return null;
    return (
      <span style={{ color: '#64748b', fontSize: 12, lineHeight: 1.35 }}>
        {t('orders:supplierOrders.form.defaultPurchaseApplied', {
          amount: formatPurchaseAmount(record.defaultPurchasePrice || 0, record.defaultPurchaseCurrency || 'PLN'),
        })}
      </span>
    );
  };

  const renderLinePlnContext = (record: EditableItem) => {
    const lineCurrency = record.currency || supplierOrder.currency || 'PLN';
    if (editing || lineCurrency === 'PLN' || record.totalCostPLN == null) return null;

    const meta = [
      record.exchangeRateToPLN
        ? `${t('orders:supplierOrders.detail.lineExchangeRate')}: ${record.exchangeRateToPLN}`
        : null,
      record.exchangeRateToPLNEffectiveDate
        ? `${t('orders:supplierOrders.detail.exchangeRateEffectiveDate')}: ${formatDate(record.exchangeRateToPLNEffectiveDate)}`
        : null,
      record.exchangeRateToPLNSource || null,
      record.exchangeRateToPLNTableNumber
        ? `${t('orders:supplierOrders.detail.exchangeRateTable')}: ${record.exchangeRateToPLNTableNumber}`
        : null,
    ].filter(Boolean);

    return (
      <Space direction="vertical" size={0}>
        <span style={{ color: '#475569', fontSize: 12 }}>
          {t('orders:supplierOrders.detail.lineValuePLN')}: {formatPurchaseAmount(record.totalCostPLN, 'PLN')}
        </span>
        {meta.length > 0 && (
          <span style={{ color: '#64748b', fontSize: 12 }}>{meta.join(' · ')}</span>
        )}
      </Space>
    );
  };

  const getLeftoverBlockReason = (record: EditableItem): string | null => {
    if (!record.id) return t('orders:supplierOrders.detail.leftoverMissingSavedItem');
    const receipt = record.leftoverReceipt;
    if (!receipt) return t('orders:supplierOrders.detail.leftoverPreviewUnavailable');
    if (receipt.eligible) return null;

    switch (receipt.blockReasonCode) {
      case 'MISSING_PRODUCT_VARIANT':
        return t('orders:supplierOrders.detail.leftoverMissingVariant');
      case 'UNSUPPORTED_UNIT':
        return t('orders:supplierOrders.detail.leftoverUnsupportedUnit');
      case 'UNSUPPORTED_VARIANT_UNIT':
        return t('orders:supplierOrders.detail.leftoverUnsupportedVariantUnit');
      case 'NO_LEFTOVER':
        return t('orders:supplierOrders.detail.leftoverNone');
      case 'UNQUANTIFIED_ALLOCATION':
        return t('orders:supplierOrders.detail.leftoverUnquantifiedAllocation');
      case 'PZ_EXISTS':
        return t('orders:supplierOrders.detail.leftoverPZExists', { pzNumber: receipt.pzNumber || '-' });
      case 'PZ_QUANTITY_LIMIT':
        return t('orders:supplierOrders.detail.leftoverQuantityLimit');
      case 'PZ_UNIT_COST_LIMIT':
        return t('orders:supplierOrders.detail.leftoverUnitCostLimit');
      case 'PZ_TOTAL_VALUE_LIMIT':
        return t('orders:supplierOrders.detail.leftoverTotalValueLimit');
      default:
        return receipt.blockReason || t('orders:supplierOrders.detail.leftoverPreviewUnavailable');
    }
  };

  const columns: TableColumnsType<EditableItem> = [
    // Product identity and catalog action stay together. The compact picker is
    // a secondary action instead of a competing column.
    {
      title: t('orders:orderDetail.product'),
      dataIndex: 'name',
      key: 'name',
      width: ITEM_COLUMN_WIDTHS.product,
      fixed: 'left',
      render: (_value: string, record) => {
        const dimensions = formatProductDimensions(record);

        if (editing) {
          return (
            <div className="supplier-order-items__product-editor">
              <Tooltip title={record.displayName || undefined} trigger={['hover', 'focus']}>
                <Input.TextArea
                  autoSize={PRODUCT_NAME_TEXTAREA_AUTO_SIZE}
                  value={record.displayName}
                  onChange={(event) => updateRow(record.key, {
                    name: event.target.value,
                    displayName: event.target.value,
                  })}
                  placeholder={t('orders:orderDetail.productName')}
                  aria-label={t('orders:orderDetail.productName')}
                  className="supplier-order-items__product-name-input"
                />
              </Tooltip>
              <div className="supplier-order-items__product-meta">
                <div className="supplier-order-items__catalog-picker">
                  <VariantPicker
                    allowCreate
                    size="small"
                    purchasePriceScope="supplier-order"
                    value={record.productVariantId || undefined}
                    showInventory={false}
                    triggerLabel={record.productVariantId
                      ? t('orders:orderDetail.variantPickerChange')
                      : t('orders:supplierOrders.form.selectProductVariant')}
                    placeholder={t('orders:supplierOrders.form.selectProductVariant')}
                    onChange={(variantId, variant) => handleVariantChange(record.key, variantId, variant)}
                  />
                </div>
                {dimensions && (
                  <span className="supplier-order-items__dimensions">
                    {dimensions}
                  </span>
                )}
              </div>
            </div>
          );
        }

        return (
          <div className="supplier-order-items__product-readonly">
            <Tooltip title={record.displayName || undefined} trigger={['hover', 'focus']}>
              <span
                className="supplier-order-items__product-name"
                tabIndex={record.displayName ? 0 : undefined}
                aria-label={record.displayName || undefined}
              >
                {record.displayName}
              </span>
            </Tooltip>
            <div className="supplier-order-items__product-meta">
              {record.productVariantId ? (
                <Tag color="blue" bordered={false}>
                  {t('orders:supplierOrders.detail.variantLinked')}
                </Tag>
              ) : (
                <span className="supplier-order-items__variant-missing">
                  {t('orders:supplierOrders.detail.variantMissing')}
                </span>
              )}
              {dimensions && (
                <span className="supplier-order-items__dimensions">
                  {dimensions}
                </span>
              )}
            </div>
          </div>
        );
      },
    },
    // 2. Ilość — unit suffix preserved as a small grey tail (not a separate column)
    {
      title: t('orders:orderDetail.quantity'),
      dataIndex: 'quantity',
      key: 'quantity',
      width: ITEM_COLUMN_WIDTHS.quantity,
      render: (value: number, record) => {
        const unitLabel = formatSupplierOrderUnit(record.unit || 'pcs', t, i18n.language);
        if (editing) {
          return (
            <Space size={4}>
              {/* No min/max clamp: out-of-range entries stay visible in the cell
                  and validate() blocks save with a message instead. */}
              <InputNumber
                step={0.001}
                precision={3}
                value={record.quantity}
                onChange={(v) => updateRow(record.key, { quantity: Number(v ?? 0) })}
                style={{ width: 110 }}
              />
              <span style={{ color: '#94a3b8', fontSize: 12 }}>{unitLabel}</span>
            </Space>
          );
        }
        return (
          <Space size={4}>
            <span>{formatSupplierOrderQuantityValue(value, i18n.language)}</span>
            <span style={{ color: '#94a3b8', fontSize: 12 }}>
              {unitLabel}
            </span>
          </Space>
        );
      },
    },
    // 3. Supplier purchase unit cost.
    ...(canViewCosts
      ? [
          {
            title: t('orders:supplierOrders.form.unitCost'),
            dataIndex: 'unitCost',
            key: 'unitCost',
            width: ITEM_COLUMN_WIDTHS.unitCost,
            render: (value: number | undefined, record: EditableItem) =>
              editing ? (
                <Space direction="vertical" size={2} style={{ maxWidth: 260 }}>
                  <InputNumber
                    step={0.01}
                    value={record.unitCost}
                    onChange={(v) => updateRow(record.key, { unitCost: Number(v ?? 0) })}
                    style={{ width: 110 }}
                  />
                  {renderDefaultPurchaseHint(record)}
                </Space>
              ) : (
                value === undefined ? '-' : formatPurchaseAmount(value, record.currency || supplierOrder.currency)
              ),
          },
        ]
      : []),
    // 4. Supplier purchase currency.
    ...(canViewCosts
      ? [
          {
            title: t('orders:supplierOrders.form.purchaseCurrency'),
            key: 'currency',
            width: ITEM_COLUMN_WIDTHS.currency,
            render: (_value: unknown, record: EditableItem) =>
              editing ? (
                <Select
                  size="small"
                  value={record.currency || supplierOrder.currency || 'PLN'}
                  onChange={(currency) => updateRow(record.key, { currency })}
                  style={{ width: 92 }}
                  aria-label={t('orders:supplierOrders.form.purchaseCurrency')}
                >
                  {ORDER_CURRENCIES.map((currency) => (
                    <Select.Option key={currency} value={currency}>
                      {currency}
                    </Select.Option>
                  ))}
                </Select>
              ) : (
                record.currency || supplierOrder.currency || '-'
              ),
          },
        ]
      : []),
    // 5. Supplier purchase total.
    ...(canViewCosts
      ? [
          {
            title: t('orders:wizard.summary.purchaseLineValue'),
            key: 'totalPrice',
            width: ITEM_COLUMN_WIDTHS.total,
            render: (_value: unknown, record: EditableItem) =>
              record.unitCost === undefined
                ? '-'
                : (
                  <Space direction="vertical" size={0}>
                    <span>{formatPurchaseAmount(record.quantity * record.unitCost, record.currency || supplierOrder.currency)}</span>
                    {renderLinePlnContext(record)}
                  </Space>
                ),
          },
        ]
      : []),
    ...(!editing && onCreateLeftoverPZ
      ? [
          {
            title: t('orders:supplierOrders.detail.leftoverToPZ'),
            key: 'leftoverToPZ',
            width: ITEM_COLUMN_WIDTHS.leftover,
            render: (_value: unknown, record: EditableItem) => {
              const blockReason = getLeftoverBlockReason(record);
              const isMissingVariant = record.leftoverReceipt?.blockReasonCode === 'MISSING_PRODUCT_VARIANT';
              const leftoverQuantity = Math.max(0, Number(record.leftoverReceipt?.quantity || 0));
              const button = (
                <Button
                  size="small"
                  icon={<InboxOutlined />}
                  disabled={Boolean(blockReason)}
                  loading={leftoverCreatingItemId === record.id}
                  onClick={() => record.id && onCreateLeftoverPZ(record.id)}
                >
                  {t('orders:supplierOrders.detail.leftoverCreatePZ', {
                    quantity: formatSupplierOrderQuantityValue(leftoverQuantity, i18n.language),
                  })}
                </Button>
              );

              return blockReason ? (
                <Space direction="vertical" size={4} style={{ maxWidth: 220 }}>
                  <Tooltip title={blockReason}>
                    <span>{button}</span>
                  </Tooltip>
                  {isMissingVariant && (
                    <span style={{ color: '#64748b', fontSize: 12, lineHeight: 1.35 }}>
                      {blockReason}
                    </span>
                  )}
                </Space>
              ) : button;
            },
          },
        ]
      : []),
    ...(editing
      ? [
          {
            title: '',
            key: 'actions',
            width: ITEM_COLUMN_WIDTHS.actions,
            fixed: 'right' as const,
            // Hide (not disable) the delete control on the last remaining row —
            // mirrors OrderProductsSection's behavior so the two pages render
            // 1:1 in edit mode.
            render: (_value: unknown, record: EditableItem) =>
              dataSource.length > 1 ? (
                <Tooltip title={t('common:delete')}>
                  <Button
                    type="text"
                    danger
                    size="small"
                    icon={<DeleteOutlined />}
                    onClick={() => removeRow(record.key)}
                  />
                </Tooltip>
              ) : null,
          },
        ]
      : []),
  ];

  const totalCostsByCurrency = dataSource.reduce<Record<string, number>>((totals, row) => {
    const currency = row.currency || supplierOrder.currency || 'PLN';
    totals[currency] = (totals[currency] || 0) + row.quantity * (row.unitCost ?? 0);
    return totals;
  }, {});
  const totalCostEntries = Object.entries(totalCostsByCurrency);
  const totalCostPLN = dataSource.reduce((sum, row) => {
    if (row.totalCostPLN != null) return sum + Number(row.totalCostPLN || 0);
    const currency = row.currency || supplierOrder.currency || 'PLN';
    return currency === 'PLN' ? sum + row.quantity * (row.unitCost ?? 0) : sum;
  }, 0);

  return (
    <Card
      title={renderSectionTitle(t('orders:supplierOrders.detail.items'))}
      style={{ ...SECTION_CARD_STYLE, marginBottom: 16 }}
      headStyle={SECTION_CARD_HEAD_STYLE}
      bodyStyle={SECTION_CARD_BODY_STYLE}
      extra={
        canEdit ? (
          editing ? (
            <Space>
              <Button type="primary" size="small" icon={<SaveOutlined />} loading={saving} onClick={handleSave}>
                {t('common:save')}
              </Button>
              <Button size="small" icon={<CloseOutlined />} disabled={saving} onClick={cancelEditing}>
                {t('common:cancel')}
              </Button>
            </Space>
          ) : (
            <Button size="small" icon={<EditOutlined />} onClick={startEditing}>
              {t('orders:supplierOrders.detail.editItems')}
            </Button>
          )
        ) : null
      }
    >
      {canViewSupplierPurchasePrices && purchasePricesIncomplete && (
        <Alert
          type="warning"
          showIcon
          message={t('orders:supplierOrders.detail.purchasePricesIncompleteTitle')}
          description={t('orders:supplierOrders.detail.purchasePricesIncompleteDescription')}
          action={
            canEdit && !editing ? (
              <Button size="small" type="primary" onClick={startEditing}>
                {t('orders:supplierOrders.detail.editItems')}
              </Button>
            ) : undefined
          }
          style={{ marginBottom: 16 }}
        />
      )}
      <div className="supplier-order-items__table-shell" data-testid="supplier-order-items-table-shell">
        <Table<EditableItem>
          rowKey="key"
          size="middle"
          pagination={false}
          dataSource={dataSource}
          columns={columns}
          tableLayout="fixed"
          scroll={{ x: tableScrollWidth }}
          locale={{ emptyText: t('orders:supplierOrders.detail.noItems') }}
          summary={() => canViewCosts ? (
            <Table.Summary.Row>
              <Table.Summary.Cell index={0} colSpan={4}>
                <strong>{t('orders:orderDetail.total')}</strong>
              </Table.Summary.Cell>
              <Table.Summary.Cell index={4}>
                <Space direction="vertical" size={0}>
                  {totalCostEntries.map(([currency, amount]) => (
                    <strong key={currency}>{formatPurchaseAmount(amount, currency)}</strong>
                  ))}
                  {!editing && totalCostPLN > 0 && (
                    <span style={{ color: '#475569', fontSize: 12 }}>
                      {t('orders:supplierOrders.detail.totalValuePLN')}: {formatPurchaseAmount(totalCostPLN, 'PLN')}
                    </span>
                  )}
                </Space>
              </Table.Summary.Cell>
              {(editing || onCreateLeftoverPZ) && <Table.Summary.Cell index={5} />}
            </Table.Summary.Row>
          ) : null}
        />
      </div>
      {editing && (
        <div style={{ marginTop: 12 }}>
          <Button type="dashed" icon={<PlusOutlined />} disabled={dataSource.length >= MAX_ITEMS} onClick={addRow}>
            {t('orders:orderDetail.addProduct')}
          </Button>
        </div>
      )}
    </Card>
  );
};

export default SupplierOrderItemsSection;
